import { NgView } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { AuthService } from "@app/services/auth.service";
import { QuoteService } from "@app/services/quote.service";
import { SfdcService } from "@app/services/sfdc.service";
import { CrudService } from "@app/services/crud.service";
import { SignalrService } from "@app/services/signalr.service";
import { StorageService } from "@app/services/storage.service";
import { eEnvironment, INotification, IPagedResult, IPendingApproval, IQuote, IRootScope } from "@models";
import { events } from "@tools";

export interface IPortalModel {
    quote?: IQuote;
    mobileMenuVisible?: boolean;
}

export interface IPortalScope extends ng.IScope {
    model: IPortalModel;
    sfdcService: SfdcService;

    logout: () => void;
    login: () => void;
    companySubdomains: string[];
    browseToSubdomain: (subdomain: string) => void;
    environments: string[];
    browseToEnvironment: (env: string) => void;

    showMobileMenu: () => void;
    hideMobileMenu: () => void;
    goToQuote: () => void;
    mobileKeyboard: boolean; // tracks whether a mobile keyboard is being shown
    readNotification: (notification: INotification, navigate?: boolean) => void;
    markAllNotificationsAsRead: () => void;
    notifications: INotification[];
    pendingApprovals: IPendingApproval[];

    newNotificationCount: () => number;
}

@NgView({
    token: Token.PortalView,
    dependencies: [
        Token.$Scope,
        Token.$Http,
        Token.AuthService,
        Token.$RootScope,
        Token.$Window,
        Token.QuoteService,
        Token.$Location,
        Token.SignalRService,
        Token.SfdcService,
        Token.StorageService,
        Token.CrudService
    ]
})
export class PortalController {
    constructor(
        private $scope: IPortalScope,
        private $http: ng.IHttpService,
        private authService: AuthService,
        private $rootScope: IRootScope,
        private $window: ng.IWindowService,
        private quoteService: QuoteService,
        private $location: ng.ILocationService,
        private signalrService: SignalrService,
        private sfdcService: SfdcService,
        private storageService: StorageService,
        private agg: CrudService
    ) {


        $scope.model = {};
        $scope.notifications = [];
        $scope.pendingApprovals = [];
        $scope.sfdcService = sfdcService;

        $scope.$watch(() => quoteService.quote, opp => {
            $scope.model.quote = opp;
        });

        $scope.goToQuote = () => {
            quoteService.goToQuote();
        };

        $scope.login = () => {
            $rootScope.user = null;
            $rootScope.$broadcast(events.loginRequested);
        };

        $scope.logout = () => {
            authService.logout();
        };

        $scope.showMobileMenu = () => {
            $scope.model.mobileMenuVisible = true;
        };

        $scope.hideMobileMenu = () => {
            $scope.model.mobileMenuVisible = false;
        };

        let refreshNotifications = () => {
            $http.get<IPagedResult<INotification>>(
                "/api/notifications",
                {
                    params: { pageSize: 10 },
                    ignoreSecurity: true
                }
            ).then(r => {
                $scope.notifications.clear();
                $scope.notifications.pushArray(r.data.items);
            });
        };

        let onNotificationSignal = () => {

            // fill in notifications
            refreshNotifications();
        };

        let offOnLogin = $rootScope.$on(events.loginSuccessful, () => {
            if ($rootScope.user.id != -1) {
                this.signalrService.notificationInvoke(
                    "subscribe",
                    this.$rootScope.company.uniqueName,
                    this.$rootScope.user.id
                );
                this.signalrService.notificationOn("notification", onNotificationSignal);
            }
        });

        $scope.$on("$destroy", () => {
            offOnLogin();
            if ($rootScope.user.id != -1) {
                this.signalrService.notificationInvoke(
                    "unsubscribe",
                    this.$rootScope.company.uniqueName,
                    this.$rootScope.user.id
                );
                this.signalrService.notificationOff("notification", onNotificationSignal);
            }
        });

        authService.authPromise.then(() => {
            // fill in company subdomains to drive the company selection for admins
            if ($rootScope.user.isAdmin) {
                $http.get<string[]>("/api/companies/subdomains").then(r => {
                    $scope.companySubdomains = r.data;
                });
            }

            $scope.browseToSubdomain = subdomain => {
                let segments = $window.location.host.split(".");
                $window.location.href = "https://" + subdomain + "." + segments[1] + "." + segments[2];
            };

            if ($rootScope.user.id != -1) {
                const cutoff = new Date();
                cutoff.setMonth(cutoff.getMonth() - 6); // 6 months ago

                // fill in pending approvals
                $http.get<IPagedResult<IPendingApproval>>(
                    "/api/pendingapprovals",
                    {
                        params: { pageSize: 4, fromDate: cutoff },
                        ignoreSecurity: true
                    }
                ).then(r => {
                    $scope.pendingApprovals.pushArray(r.data.items);
                });

                // fill in notifications
                refreshNotifications();
            }
        });

        // fill in environments for environment selector
        $scope.environments = [];
        $scope.environments.push(eEnvironment.dev);
        $scope.environments.push(eEnvironment.test);
        $scope.environments.push(eEnvironment.prod);

        $scope.browseToEnvironment = (env: string) => {
            if (env == $rootScope.environment) return;

            let envSuffix = "";
            if (env != eEnvironment.prod) {
                envSuffix = "-" + env;
            }
            let segments = $window.location.host.split(".");
            $window.location.href =
                "https://" + $rootScope.company.subdomain + envSuffix + "." + segments[1] + "." + segments[2];
        };

        let focusin = e => {
            let phase = $rootScope.$$phase;
            if (phase == "$apply" || phase == "$digest") {
                $scope.mobileKeyboard = true;
            } else {
                $scope.$apply(() => {
                    $scope.mobileKeyboard = true;
                });
            }
        };

        let focusout = e => {
            let phase = $rootScope.$$phase;
            if (phase == "$apply" || phase == "$digest") {
                $scope.mobileKeyboard = false;
            } else {
                $scope.$apply(() => {
                    $scope.mobileKeyboard = false;
                });
            }
        };
        $(document).on("focusin", "input[type='text']", focusin);
        $(document).on("focusout", "input[type='text']", focusout);

        $scope.readNotification = (notification, navigate: boolean = true) => {
            $http.post("/api/notifications/markasread", [notification.id]);
            // mark as read locally too
            $scope.notifications.find(n => {
                return n.id == notification.id;
            }).read = true;
            if (navigate) $location.path(notification.urlPath);
        };

        $scope.markAllNotificationsAsRead = () => {
            $scope.notifications.forEach(n => {
                $scope.readNotification(n, false);
            });
        };

        $scope.newNotificationCount = () => {
            return $scope.pendingApprovals.length + $scope.notifications.filter(n => !n.read).length;
        };

        $scope.$on("$destroy", () => {
            $(document).off("focusin", "input[type='text']", focusin);
            $(document).off("focusout", "input[type='text']", focusout);
        });

        if ($rootScope.context.company.snapStudioMode || (window as any).__kbHostedByVue) {
            $rootScope.hideHeader = true;
        }

    }
}
